import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { ArrowLeft, Save, Loader2, Check, BookOpen } from 'lucide-react';

const subjectIcons = { Math: '📐', Science: '🧬', English: '📖', 'Social Science': '🌍', Hindi: '🔤', Physics: '⚛️', Chemistry: '🧪', Biology: '🧬' };

export default function TeacherDashboard({ onNavigate }) {
  const { api, user } = useAuth();
  const [dashData, setDashData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedClass, setSelectedClass] = useState('Class 8');
  const [selectedSubject, setSelectedSubject] = useState('Math');
  const [completedChapters, setCompletedChapters] = useState([]);
  const [nextChapter, setNextChapter] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [chapters, setChapters] = useState([]);
  const [loadingChapters, setLoadingChapters] = useState(false);

  const classes = ['Class 6', 'Class 7', 'Class 8', 'Class 9', 'Class 10', 'Class 11', 'Class 12'];
  const subjects = ['Math', 'Science', 'English', 'Social Science', 'Hindi', 'Physics', 'Chemistry', 'Biology'];
  
  // Dynamic title based on user role
  const isParent = user?.role === 'parent';
  const pageTitle = isParent ? 'Progress Tracker' : 'Teacher Dashboard';
  const pageSubtitle = isParent ? "Track and update your child's chapter progress" : 'Set chapter progress for your class';

  // Fetch chapters for selected class and subject
  const fetchChapters = useCallback(async () => {
    setLoadingChapters(true);
    try {
      const res = await api.get(`/chapters?class_name=${encodeURIComponent(selectedClass)}&subject=${encodeURIComponent(selectedSubject)}`);
      // Sort chapters by number (NCERT curriculum order)
      const sortedChapters = (res.data || []).sort((a, b) => a.number - b.number);
      setChapters(sortedChapters);
    } catch (err) {
      console.error('Error fetching chapters:', err);
      setChapters([]);
    }
    setLoadingChapters(false);
  }, [api, selectedClass, selectedSubject]);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await api.get('/teacher/dashboard');
      setDashData(res.data);
      
      // Load existing progress
      const existing = res.data.progress_set?.find(
        p => p.class_name === selectedClass && p.subject === selectedSubject
      );
      if (existing) {
        setCompletedChapters(existing.completed_chapters || []);
        setNextChapter(existing.next_chapter);
      } else {
        setCompletedChapters([]);
        setNextChapter(null);
      }
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [api, selectedClass, selectedSubject]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);
  useEffect(() => { fetchChapters(); }, [fetchChapters]);

  const toggleChapter = (num) => {
    setCompletedChapters(prev => {
      if (prev.includes(num)) return prev.filter(n => n !== num);
      return [...prev, num].sort();
    });
    setSaved(false);
  };

  const saveProgress = async () => {
    setSaving(true);
    try {
      await api.post('/school/progress', {
        class_name: selectedClass,
        subject: selectedSubject,
        completed_chapters: completedChapters,
        next_chapter: nextChapter || (Math.max(0, ...completedChapters) + 1),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error(err);
    }
    setSaving(false);
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-violet-500/30 border-t-violet-500 rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="pb-nav px-4 pt-6 max-w-lg mx-auto space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button data-testid="back-from-teacher" onClick={() => onNavigate('dashboard')} className="text-slate-400 hover:text-white">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-white font-['Outfit']" data-testid="teacher-title">{pageTitle}</h1>
          <p className="text-xs text-slate-500">{pageSubtitle}</p>
        </div>
      </div>

      {/* Class & Subject Selection */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-5">
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 mb-1.5 block">Class</label>
            <select
              data-testid="teacher-class-select"
              value={selectedClass}
              onChange={e => { setSelectedClass(e.target.value); setCompletedChapters([]); }}
              className="w-full bg-slate-950/50 border border-slate-800 focus:border-violet-500 rounded-xl h-11 px-3 text-sm text-slate-100 outline-none appearance-none"
            >
              {classes.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 mb-1.5 block">Subject</label>
            <select
              data-testid="teacher-subject-select"
              value={selectedSubject}
              onChange={e => { setSelectedSubject(e.target.value); setCompletedChapters([]); }}
              className="w-full bg-slate-950/50 border border-slate-800 focus:border-violet-500 rounded-xl h-11 px-3 text-sm text-slate-100 outline-none appearance-none"
            >
              {subjects.map(s => <option key={s} value={s}>{subjectIcons[s]} {s}</option>)}
            </select>
          </div>
        </div>

        {/* Chapter Progress */}
        <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-violet-400" />
          {subjectIcons[selectedSubject]} {selectedSubject} - {selectedClass}
        </h3>
        <p className="text-xs text-slate-500 mb-3">Tap chapters that have been completed in class</p>

        <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
          {loadingChapters ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-violet-400" />
            </div>
          ) : chapters.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-4">No chapters available for this subject</p>
          ) : (
            chapters.map(ch => (
              <button
                key={ch.number}
                data-testid={`teacher-ch-${ch.number}`}
                onClick={() => toggleChapter(ch.number)}
                className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all ${
                  completedChapters.includes(ch.number)
                    ? 'border-emerald-500/30 bg-emerald-500/10'
                    : 'border-slate-800 bg-slate-950/30 hover:border-slate-700'
                }`}
              >
                <div className={`w-6 h-6 rounded-lg flex items-center justify-center shrink-0 ${
                  completedChapters.includes(ch.number) ? 'bg-emerald-500 text-white' : 'bg-slate-800 text-slate-500'
                }`}>
                  {completedChapters.includes(ch.number) ? <Check className="w-4 h-4" /> : <span className="text-xs">{ch.number}</span>}
                </div>
                <span className={`text-sm text-left ${completedChapters.includes(ch.number) ? 'text-emerald-300' : 'text-slate-400'}`}>
                  Ch {ch.number}: {ch.name}
                </span>
              </button>
            ))
          )}
        </div>

        {/* Save Button */}
        <button
          data-testid="save-progress-btn"
          onClick={saveProgress}
          disabled={saving}
          className={`w-full mt-4 rounded-xl h-11 font-semibold text-sm transition-all flex items-center justify-center gap-2 ${
            saved ? 'bg-emerald-600 text-white' : 'bg-violet-600 hover:bg-violet-700 text-white glow-violet'
          } disabled:opacity-50`}
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : saved ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />}
          {saved ? 'Saved!' : 'Save Progress'}
        </button>
      </div>

      {/* Previously Set Progress */}
      {dashData?.progress_set?.length > 0 && (
        <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-5">
          <h3 className="text-sm font-semibold text-white mb-3">Previously Set Progress</h3>
          <div className="space-y-2">
            {dashData.progress_set.map((p, i) => (
              <div key={i} className="flex items-center justify-between bg-slate-800/30 rounded-lg px-3 py-2" data-testid={`prev-progress-${i}`}>
                <div>
                  <p className="text-xs text-white">{subjectIcons[p.subject]} {p.subject} - {p.class_name}</p>
                  <p className="text-[10px] text-slate-500">{p.completed_chapters?.length || 0} chapters completed</p>
                </div>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map(n => (
                    <div key={n} className={`w-2 h-2 rounded-full ${
                      p.completed_chapters?.includes(n) ? 'bg-emerald-500' : 'bg-slate-700'
                    }`} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
